import { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Progress,
    Tag,
    VStack,
    HStack,
    Spinner,
    useToast,
    Grid,
    GridItem,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Flex,
    Center,
    Icon
} from '@chakra-ui/react';
import { FiBarChart2, FiDollarSign } from 'react-icons/fi';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import API from '../../../helpers/axiosInstance';
import { formatPrice, getApiHeaders } from '../../../utils/DefaultFunction';

import { PRIMARY_BG } from '../../../theme/brand';
import { getChannelLabel } from '../../../utils/channels';
export function ChannelBreakdownTab({ contactId }) {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const channelColors = {
        'WhatsApp': '#25D366',
        'SMS': '#0088cc',
        'Email': '#EA4335',
        'Push': '#f59e0b',
        'In-App': '#8b5cf6',
        'Voice': PRIMARY_BG,
        'Channel-0': '#94a3b8'
    };

    useEffect(() => {
        fetchChannelBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId]);

    const fetchChannelBreakdown = async () => {
        setLoading(true);
        try {
            const url = `/contacts/${contactId}/channel-breakdown`;
            const response = await API.get(url, { headers: getApiHeaders() });

            if (response.data.success) {
                setApiData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching channel breakdown:", error);
            toast({
                title: "Error",
                description: "Failed to fetch channel breakdown",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" py={10}>
                <Spinner size="xl" />
            </Box>
        );
    }

    if (!apiData) {
        return (
            <Center minH="400px">
                <VStack spacing={3}>
                    <Icon as={FiBarChart2} boxSize={12} color="gray.300" />
                    <Text fontSize="md" fontWeight="medium" color="gray.500">
                        No Data Available
                    </Text>
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                        Channel breakdown data is not available
                    </Text>
                </VStack>
            </Center>
        );
    }

    const formatDateRange = (from, to) => {
        const fromDate = new Date(from);
        const toDate = new Date(to);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return `${fromDate.toLocaleDateString('en-GB', options)} - ${toDate.toLocaleDateString('en-GB', options)}`;
    };

    const formatMonth = (monthStr) => {
        const [year, month] = monthStr.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    // Check if trend data is available
    const hasTrendData = apiData.trend_12_month?.channels?.some(ch => ch.points.some(p => p.messages > 0));

    // Prepare 12-month trend chart
    const trendChartOptions = {
        chart: {
            type: 'line',
            height: 400
        },
        title: {
            text: null
        },
        xAxis: {
            categories: apiData.trend_12_month?.months?.map(m => formatMonth(m)) || []
        },
        yAxis: {
            title: {
                text: 'Messages'
            }
        },
        tooltip: {
            shared: true,
            crosshairs: true
        },
        plotOptions: {
            line: {
                marker: {
                    enabled: true,
                    radius: 4
                }
            }
        },
        series: (apiData.trend_12_month?.channels || [])
            .filter(ch => ch.points.some(p => p.sent > 0))
            .map(channel => ({
                name: getChannelLabel(channel.channel, { short: true }),
                data: channel.points.map(p => p.sent),
                color: channelColors[channel.channel] || '#666'
            })),
        credits: {
            enabled: false
        }
    };

    // Prepare message type pie chart for each channel
    const getMessageTypeChartOptions = (channelData) => {
        const filteredTypes = channelData.types.filter(t => t.messages > 0);

        if (filteredTypes.length === 0) {
            return null;
        }

        return {
            chart: {
                type: 'pie',
                height: 280
            },
            title: {
                text: null
            },
            tooltip: {
                pointFormat: '<b>{point.percentage:.1f}%</b><br/>Messages: {point.y}'
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    dataLabels: {
                        enabled: true,
                        format: '<b>{point.name}</b><br/>{point.percentage:.1f}%',
                        style: {
                            fontSize: '11px'
                        }
                    }
                }
            },
            series: [{
                name: 'Message Types',
                colorByPoint: true,
                data: filteredTypes.map(t => ({
                    name: t.type,
                    y: t.messages
                }))
            }],
            credits: {
                enabled: false
            }
        };
    };

    const activeChannels = (apiData.message_type_breakdown_by_channel || []).filter(ch => ch.totals.messages > 0);

    // Check if cost comparison data is available
    const hasCostData = activeChannels.length > 0 && activeChannels.some(ch => ch.totals.total_cost > 0);

    // Cost comparison bar chart
    const costComparisonChartOptions = {
        chart: {
            type: 'column',
            height: 400
        },
        title: {
            text: null
        },
        xAxis: {
            categories: activeChannels.map(ch => getChannelLabel(ch.channel, { short: true }))
        },
        yAxis: {
            title: {
                text: `Total Cost ${apiData?.totals?.currency_code || "$"}`
            }
        },
        
        tooltip: {
            formatter: function () {
                return '<b>' + this.x + '</b><br/>' +
                    `Total Cost: <b>` + formatPrice(this.y,"en-US",apiData?.totals?.currency_code)  + '</b>';
            }
        },
        plotOptions: {
            column: {
                colorByPoint: true
            }
        },
        colors: activeChannels.map(ch => channelColors[ch.channel] || '#666'),
        series: [{
            name: 'Total Cost',
            data: activeChannels.map(ch => ch.totals.total_cost),
            showInLegend: false
        }],
        credits: {
            enabled: false
        }
    };

    return (
        <VStack spacing={6} align="stretch">
            {/* Summary Cards */}
            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                <GridItem>
                    <Box className='box-style-card' h="120px">
                        <Box>
                            <Stat>
                                <StatLabel fontSize="xs" color="gray.500" fontWeight="medium">Total Messages</StatLabel>
                                <StatNumber fontSize="2xl" fontWeight="bold">{apiData.totals?.total_messages?.toLocaleString() || 0}</StatNumber>
                                <StatHelpText fontSize="xs" mb={0}>All channels</StatHelpText>
                            </Stat>
                        </Box>
                    </Box>
                </GridItem>

                <GridItem>
                    <Box className='box-style-card' h="120px">
                        <Box>
                            <Stat>
                                <StatLabel fontSize="xs" color="gray.500" fontWeight="medium">Total Cost</StatLabel>
                                <StatNumber fontSize="2xl" fontWeight="bold" color="#25D366">
                                    {apiData?.totals?.currency_code || "$"} {apiData.totals?.total_cost?.toFixed(2) || '0.00'}
                                </StatNumber>
                                <StatHelpText fontSize="xs" mb={0}>All channels</StatHelpText>
                            </Stat>
                        </Box>
                    </Box>
                </GridItem>

                <GridItem>
                    <Box className='box-style-card' h="120px">
                        <Box>
                            <Stat>
                                <StatLabel fontSize="xs" color="gray.500" fontWeight="medium">Most Used Channel</StatLabel>
                                {apiData.most_used?.channel ? (
                                    <>
                                        <StatNumber fontSize="xl" fontWeight="bold">
                                            <HStack>
                                                <Box w={3} h={3} borderRadius="full" bg={channelColors[apiData.most_used.channel]} />
                                                <Text>{getChannelLabel(apiData.most_used.channel, { short: true })}</Text>
                                            </HStack>
                                        </StatNumber>
                                        <StatHelpText fontSize="xs" mb={0}>
                                            {apiData.most_used.messages} messages (All channel)
                                        </StatHelpText>
                                    </>
                                ) : (
                                    <StatNumber fontSize="xl" fontWeight="bold" color="gray.400">N/A</StatNumber>
                                )}
                            </Stat>
                        </Box>
                    </Box>
                </GridItem>

                <GridItem>
                    <Box className='box-style-card' h="120px">
                        <Box>
                            <Stat>
                                <StatLabel fontSize="xs" color="gray.500" fontWeight="medium">Most Expensive Channel</StatLabel>
                                {apiData.most_expensive?.channel ? (
                                    <>
                                        <StatNumber fontSize="xl" fontWeight="bold">
                                            <HStack>
                                                <Box w={3} h={3} borderRadius="full" bg={channelColors[apiData.most_expensive.channel]} />
                                                <Text>{getChannelLabel(apiData.most_expensive.channel, { short: true })}</Text>
                                            </HStack>
                                        </StatNumber>
                                        <StatHelpText fontSize="xs" mb={0}>
                                            {apiData?.totals?.currency_code || "$"} {apiData.most_expensive.total_cost}
                                        </StatHelpText>
                                    </>
                                ) : (
                                    <StatNumber fontSize="xl" fontWeight="bold" color="gray.400">N/A</StatNumber>
                                )}
                            </Stat>
                        </Box>
                    </Box>
                </GridItem>
            </Grid>

            {/* 12-Month Trend Chart */}
            <Box className='box-style-card'>
                <Box>
                    <Heading size="md">12-Month Channel Usage Trend</Heading>
                    <Text color="gray.600" fontSize="sm">Message volume by channel over the last year</Text>
                </Box>
                <Box>
                    {hasTrendData ? (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={trendChartOptions}
                        />
                    ) : (
                        <Center minH="400px">
                            <VStack spacing={3}>
                                <Icon as={FiBarChart2} boxSize={12} color="gray.300" />
                                <Text fontSize="md" fontWeight="medium" color="gray.500">
                                    No Trend Data Available
                                </Text>
                                <Text fontSize="sm" color="gray.400" textAlign="center">
                                    No channel usage data for the last 12 months
                                </Text>
                            </VStack>
                        </Center>
                    )}
                </Box>
            </Box>

            {/* Channel Breakdown */}
            {activeChannels.length > 0 ? (
                <VStack spacing={4} align="stretch">
                    {activeChannels.map((channelData) => {
                        const activeTypes = channelData.types.filter(type => type.messages > 0);

                        return (
                            <Box key={channelData.channel_id} className='box-style-card'>
                                <Box px={2} py={4}>
                                    <Flex justify="space-between" align="center">
                                        <HStack>
                                            <Box w={4} h={4} borderRadius="full" bg={channelColors[channelData.channel]} />
                                            <Text fontSize="lg" fontWeight="500">{getChannelLabel(channelData.channel, { short: true })}</Text>
                                        </HStack>
                                        <HStack spacing={8}>
                                            <Box textAlign="right">
                                                <Text fontSize="xs" color="gray.500" fontWeight="medium">Total Messages</Text>
                                                <Text fontSize="lg" fontWeight="500">{channelData.totals.messages.toLocaleString()}</Text>
                                            </Box>
                                            <Box textAlign="right">
                                                <Text fontSize="xs" color="gray.500" fontWeight="medium">Total Cost</Text>
                                                <Text fontSize="lg" fontWeight="500" color="#25D366">
                                                    {apiData?.totals?.currency_code || "$"} {channelData.totals.total_cost.toFixed(2)}
                                                </Text>
                                            </Box>
                                            <Box textAlign="right">
                                                <Text fontSize="xs" color="gray.500" fontWeight="medium">Cost/Message</Text>
                                                <Text fontSize="lg" fontWeight="500">
                                                    {apiData?.totals?.currency_code || "$"} {channelData.totals.cost_per_message.toFixed(4)}
                                                </Text>
                                            </Box>
                                        </HStack>
                                    </Flex>
                                </Box>
                                <Box px={2} py={4}>
                                    {activeTypes.length > 0 ? (
                                        <VStack spacing={3} align="stretch">
                                            <Text fontSize="sm" fontWeight="500" color="black">Message Type Breakdown</Text>

                                            {/* Message Type Cards Grid */}
                                            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                                                {activeTypes.map((type) => (
                                                    <Box
                                                        key={type.type_id}
                                                        bg="#fbf9fa"
                                                        className='box-style-card'
                                                        borderRadius="lg"
                                                        p={3}
                                                    >
                                                        <Flex justify="space-between" align="center" mb={2}>
                                                            <Text fontSize="sm" fontWeight="500" color="black">
                                                                {type.type}
                                                            </Text>
                                                            <Tag
                                                                size="sm"
                                                                bg="white"
                                                                color="black"
                                                                border="1px solid #0000001a"
                                                            >
                                                                {type.percent.toFixed(1)}%
                                                            </Tag>
                                                        </Flex>

                                                        <VStack spacing={1} align="stretch">
                                                            <Flex justify="space-between" align="center">
                                                                <Text fontSize="xs" color="gray.600">Count</Text>
                                                                <Text fontSize="sm" fontWeight="semibold">
                                                                    {type.messages.toLocaleString()}
                                                                </Text>
                                                            </Flex>
                                                            <Flex justify="space-between" align="center">
                                                                <Text fontSize="xs" color="gray.600">Cost</Text>
                                                                <Text fontSize="sm" fontWeight="500" color="#00a63e">
                                                                    {apiData?.totals?.currency_code || "$"} {type.type_total_cost.toFixed(2)}
                                                                </Text>
                                                            </Flex>
                                                        </VStack>

                                                        <Progress
                                                            value={type.percent}
                                                            size="sm"
                                                            sx={{
                                                                "& > div": {
                                                                    background: PRIMARY_BG,
                                                                },
                                                            }}
                                                            bg="#3b82f680"
                                                            borderRadius="full"
                                                            mt={2}
                                                        />
                                                    </Box>
                                                ))}
                                            </Grid>

                                            {/* Summary Row */}
                                            <Box mt={4} pt={3} borderTop="1px" borderColor="gray.200">
                                                <Grid templateColumns={`repeat(${activeTypes.length}, 1fr)`} gap={4}>
                                                    {activeTypes.map((type) => (
                                                        <Box key={type.type_id} textAlign="center">
                                                            <Text
                                                                fontSize="md"
                                                                fontWeight="500"
                                                                color={channelColors[channelData.channel]}
                                                            >
                                                                {type.messages.toLocaleString()}
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.600">
                                                                {type.type}
                                                            </Text>
                                                        </Box>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        </VStack>
                                    ) : (
                                        <Center minH="200px">
                                            <VStack spacing={2}>
                                                <Icon as={FiBarChart2} boxSize={8} color="gray.300" />
                                                <Text fontSize="sm" color="gray.500">
                                                    No message type data for this channel
                                                </Text>
                                            </VStack>
                                        </Center>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </VStack>
            ) : (
                <Box className='box-style-card' p={6}>
                    <Center minH="200px">
                        <VStack spacing={3}>
                            <Icon as={FiBarChart2} boxSize={12} color="gray.300" />
                            <Text fontSize="md" fontWeight="medium" color="gray.500">
                                No Channel Data Available
                            </Text>
                            <Text fontSize="sm" color="gray.400" textAlign="center">
                                No messages have been sent through any channels yet
                            </Text>
                        </VStack>
                    </Center>
                </Box>
            )}

            {/* Cost Comparison Chart */}
            <Box className='box-style-card'>
                <Box>
                    <Heading size="md">Channel Cost Comparison</Heading>
                    <Text color="gray.600" fontSize="sm">Total cost breakdown by channel</Text>
                </Box>
                <Box>
                    {hasCostData ? (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={costComparisonChartOptions}
                        />
                    ) : (
                        <Center minH="400px">
                            <VStack spacing={3}>
                                <Icon as={FiDollarSign} boxSize={12} color="gray.300" />
                                <Text fontSize="md" fontWeight="medium" color="gray.500">
                                    No Cost Data Available
                                </Text>
                                <Text fontSize="sm" color="gray.400" textAlign="center">
                                    No cost information available for channel comparison
                                </Text>
                            </VStack>
                        </Center>
                    )}
                </Box>
            </Box>
        </VStack>
    );
}